import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";

const WS_BASE = API.replace(/^http/, "ws");

/* ---------- Shared socket (one connection per tab, many subscribers) ---------- */
const sock = { ws: null, live: false, subs: new Set(), liveSubs: new Set(), attempts: 0, retry: null, token: null };

function emit(data) { sock.subs.forEach((fn) => { try { fn(data); } catch { /* ignore */ } }); }
function setLive(v) { if (sock.live === v) return; sock.live = v; sock.liveSubs.forEach((fn) => fn(v)); }

function ensureSocket() {
  const token = localStorage.getItem("helencia_token");
  if (!token) return;
  if (sock.ws && sock.token === token && sock.ws.readyState <= 1) return;
  sock.token = token;
  try { sock.ws = new WebSocket(`${WS_BASE}/ws/dm?token=${encodeURIComponent(token)}`); } catch { setLive(false); return; }
  const ws = sock.ws;
  ws.onopen = () => { sock.attempts = 0; setLive(true); };
  ws.onmessage = (e) => {
    try {
      const data = JSON.parse(e.data);
      if (data.type === "ping") ws.send("ping");
      emit(data);
    } catch { /* ignore */ }
  };
  ws.onclose = () => {
    setLive(false);
    if (sock.ws !== ws) return;
    sock.ws = null;
    if (!sock.subs.size || sock.attempts >= 6) return;
    sock.attempts += 1;
    clearTimeout(sock.retry);
    sock.retry = setTimeout(ensureSocket, Math.min(20000, 1500 * sock.attempts));
  };
  ws.onerror = () => { try { ws.close(); } catch { /* noop */ } };
}

function releaseSocket() {
  if (sock.subs.size) return;
  clearTimeout(sock.retry);
  try { sock.ws?.close(); } catch { /* noop */ }
  sock.ws = null;
}

/** Subscribe to live DM events {type: ready|message|connection_request|connection_accepted}. */
export function useDMSocket(onEvent) {
  const [live, setLiveState] = useState(sock.live);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;
  useEffect(() => {
    const handler = (d) => cbRef.current?.(d);
    sock.subs.add(handler);
    sock.liveSubs.add(setLiveState);
    ensureSocket();
    return () => { sock.subs.delete(handler); sock.liveSubs.delete(setLiveState); releaseSocket(); };
  }, []);
  return { live };
}

/* ---------- Shared counters store (one fetch feeds every badge) ---------- */
const counts = { value: { requests: 0, connections: 0, unread_messages: 0 }, subs: new Set(), inflight: null, timer: null, lastAt: 0 };

function refreshCounts(force = false) {
  if (counts.inflight) return counts.inflight;
  if (!force && Date.now() - counts.lastAt < 2000) return Promise.resolve();
  counts.inflight = api.get("/network/counts")
    .then(({ data }) => { counts.value = data; counts.lastAt = Date.now(); counts.subs.forEach((fn) => fn(data)); })
    .catch(() => {})
    .finally(() => { counts.inflight = null; });
  return counts.inflight;
}

export function useNetworkCounts() {
  const [value, setValue] = useState(counts.value);
  useDMSocket((ev) => { if (["message", "connection_request", "connection_accepted"].includes(ev.type)) refreshCounts(true); });
  useEffect(() => {
    counts.subs.add(setValue);
    refreshCounts();
    if (!counts.timer) counts.timer = setInterval(() => counts.subs.size && refreshCounts(true), 45000);
    const onRefresh = () => refreshCounts(true);
    window.addEventListener("dm:refresh", onRefresh);
    return () => {
      counts.subs.delete(setValue);
      window.removeEventListener("dm:refresh", onRefresh);
      if (!counts.subs.size) { clearInterval(counts.timer); counts.timer = null; }
    };
  }, []);
  return value;
}
